import type { PasskeyDto, PasskeyOptionsDto } from '@bqcrm/shared';
import { ActionIcon, Alert, Badge, Button, Group, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconFingerprint, IconPencil, IconTrash } from '@tabler/icons-react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorText } from '../api';
import { ask } from '../confirm';
import { formatDateTime } from '../format';
import { notifyError, notifyOk } from '../notify';

/** Czy przeglądarka obsługuje klucze dostępu (wymaga HTTPS albo localhost). */
export const passkeysSupported = () => typeof window !== 'undefined' && window.isSecureContext && 'PublicKeyCredential' in window;

/** Komunikat dla anulowania / błędu okna systemowego (z przyczyną podaną przez przeglądarkę — do diagnozy). */
export function passkeyError(e: unknown): string {
  const { name, message } = (e ?? {}) as { name?: string; message?: string };
  const detail = message ? `\n(przeglądarka: ${message})` : '';
  if (name === 'NotAllowedError' || name === 'AbortError') return `Anulowano albo upłynął czas — spróbuj ponownie.${detail}`;
  if (name === 'SecurityError') return `Przeglądarka nie pozwala użyć klucza pod tym adresem.${detail}`;
  if (name === 'InvalidStateError') return 'Ten klucz jest już zapisany na Twoim koncie.';
  return errorText(e);
}

/** Wyzwanie logowania kluczem pobierane z wyprzedzeniem (ważne 5 min) — kliknięcie od razu otwiera okno systemowe. */
export const usePasskeyLoginOptions = (enabled: boolean) =>
  useQuery({
    queryKey: ['passkey-login-options'],
    queryFn: () => api<PasskeyOptionsDto>('/auth/passkey/options', { method: 'POST' }),
    enabled,
    staleTime: 3 * 60_000,
    refetchInterval: 3 * 60_000,
    gcTime: 0,
    retry: false,
  });

/**
 * Logowanie kluczem dostępu: wyzwanie z serwera → okno systemowe (odcisk palca / twarz / PIN) → weryfikacja.
 * `pre` — wyzwanie pobrane wcześniej: okno systemowe musi otworzyć się zaraz po kliknięciu (Safari, Firefox
 * odrzucają je, gdy przed nim jest jeszcze zapytanie do serwera).
 */
export async function loginWithPasskey(pre?: PasskeyOptionsDto, remember = false): Promise<void> {
  const o =
    pre ??
    (await api<PasskeyOptionsDto>('/auth/passkey/options', {
      method: 'POST',
    }));
  const response = await startAuthentication({
    optionsJSON: o.options as never,
  });
  await api('/auth/passkey/login', {
    method: 'POST',
    body: { challengeId: o.challengeId, response, remember },
  });
}

/** Domyślna nazwa nowego klucza na podstawie urządzenia. */
function deviceName(): string {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'Urządzenie';
  const br = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : '';
  return br ? `${os} · ${br}` : os;
}

/** Lista kluczy dostępu własnego konta: dodawanie, zmiana nazwy, usuwanie. */
export function PasskeysSection({ enabled }: { enabled: boolean }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => api<PasskeyDto[]>('/auth/passkeys'),
    enabled,
  });
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['passkeys'] });
    void qc.invalidateQueries({ queryKey: ['users'] });
  };

  // wyzwanie rejestracji z wyprzedzeniem (ważne 5 min) — kliknięcie od razu otwiera okno systemowe
  const regOptions = useQuery({
    queryKey: ['passkey-reg-options'],
    queryFn: () => api<PasskeyOptionsDto>('/auth/passkeys/options', { method: 'POST' }),
    enabled: enabled && passkeysSupported(),
    staleTime: 3 * 60_000,
    refetchInterval: 3 * 60_000,
    gcTime: 0,
    retry: false,
  });
  const add = useMutation({
    mutationFn: async () => {
      const o =
        regOptions.data ??
        (await api<PasskeyOptionsDto>('/auth/passkeys/options', {
          method: 'POST',
        }));
      const response = await startRegistration({
        optionsJSON: o.options as never,
      });
      return api<PasskeyDto>('/auth/passkeys', {
        method: 'POST',
        body: { challengeId: o.challengeId, response, name: deviceName() },
      });
    },
    onSuccess: (k) => {
      setError(null);
      refresh();
      notifyOk(`Dodano klucz dostępu „${k.name}”. Przy następnym logowaniu wybierz „Zaloguj kluczem dostępu”.`);
    },
    onError: (e) => setError(passkeyError(e)),
    // wyzwanie jednorazowe — po próbie nowe
    onSettled: () => void qc.invalidateQueries({ queryKey: ['passkey-reg-options'] }),
  });
  const rename = useMutation({
    mutationFn: (k: { id: string; name: string }) => api(`/auth/passkeys/${k.id}`, { method: 'PUT', body: { name: k.name } }),
    onSuccess: () => (setEditing(null), refresh()),
    onError: notifyError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/auth/passkeys/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      notifyOk('Usunięto klucz dostępu');
      refresh();
    },
    onError: notifyError,
  });

  const supported = passkeysSupported();
  return (
    <Stack gap="sm">
      <div>
        <Text fw={700}>Klucze dostępu</Text>
        <Text size="sm" c="dimmed">
          Logowanie odciskiem palca, twarzą albo PIN-em telefonu lub komputera — bez hasła i bez kodu. Na komputerze bez czytnika możesz użyć telefonu (przeglądarka pokaże kod QR).
        </Text>
      </div>
      {!supported && <Alert color="yellow">Ta przeglądarka albo ten adres nie obsługuje kluczy dostępu. Klucze działają tylko pod bezpiecznym adresem (https).</Alert>}
      {error && (
        <Alert color="red" style={{ whiteSpace: "pre-line" }}>
          {error}
        </Alert>
      )}
      {list.isError && (
        <Alert color="red" title="Nie udało się pobrać listy kluczy dostępu">
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
              {errorText(list.error)}
            </Text>
            <Button size="xs" variant="light" color="red" loading={list.isFetching} onClick={() => void list.refetch()}>
              Ponów
            </Button>
          </Group>
        </Alert>
      )}
      {list.data?.map((k) => (
        <Group key={k.id} justify="space-between" wrap="nowrap" p="xs" style={{ border: '1px solid var(--app-border)', borderRadius: 8 }}>
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            <IconFingerprint size={20} style={{ flexShrink: 0 }} />
            {editing?.id === k.id ? (
              <TextInput
                size="xs"
                value={editing.name}
                onChange={(e) => setEditing({ id: k.id, name: e.currentTarget.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editing.name.trim()) rename.mutate(editing);
                  if (e.key === 'Escape') setEditing(null);
                }}
                onBlur={() => (editing.name.trim() ? rename.mutate(editing) : setEditing(null))}
                autoFocus
                maxLength={80}
                style={{ flex: 1 }}
              />
            ) : (
              <div style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} truncate>
                  {k.name}{' '}
                  {k.backedUp && (
                    <Badge size="xs" variant="light" color="teal">
                      synchronizowany
                    </Badge>
                  )}
                </Text>
                <Text size="xs" c="dimmed">
                  dodany {formatDateTime(k.createdAt)} · {k.lastUsedAt ? `ostatnio użyty ${formatDateTime(k.lastUsedAt)}` : 'jeszcze nieużyty'}
                </Text>
              </div>
            )}
          </Group>
          <Group gap={4} wrap="nowrap">
            <Tooltip label="Zmień nazwę">
              <ActionIcon variant="subtle" color="gray" onClick={() => setEditing({ id: k.id, name: k.name })} aria-label="Zmień nazwę">
                <IconPencil size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Usuń klucz">
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label="Usuń klucz"
                onClick={async () => {
                  if (
                    await ask(`Usunąć klucz „${k.name}”? Nie będzie można się nim logować. Usuń go też z urządzenia (ustawienia haseł / kluczy).`, {
                      title: 'Usunięcie klucza dostępu',
                      confirm: 'Usuń',
                      danger: true,
                    })
                  )
                    remove.mutate(k.id);
                }}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      ))}
      <Group>
        <Button leftSection={<IconFingerprint size={16} />} variant={list.data?.length ? 'default' : 'filled'} disabled={!supported} loading={add.isPending} onClick={() => add.mutate()}>
          {list.data?.length ? 'Dodaj kolejny klucz' : 'Dodaj klucz dostępu'}
        </Button>
      </Group>
    </Stack>
  );
}
